import ability from "utils/abilit";
import { PermissionType, SubjectKey } from "utils/enums";

// Define the menu structure with routes and required permissions
const menuRoutes = [
  {
    path: "/",
    subject: SubjectKey.DASHBOARD,
  },
  // Accounting routes
  {
    path: "/accounting/chart-of-accounts",
    subject: SubjectKey.ACCOUNT,
  },
  {
    path: "/accounting/transaction-listing",
    subject: SubjectKey.TRANSACTION,
  },
  {
    path: "/accounting/bank-accounts",
    subject: SubjectKey.BANK_ACCOUNT,
  },
  {
    path: "/accounting/expense-categories",
    subject: SubjectKey.EXPENSE_CATEGORY,
  },
  {
    path: "/accounting/expenses",
    subject: SubjectKey.EXPENSE,
  },
  {
    path: "/accounting/tax",
    subject: SubjectKey.TAXES,
  },
  {
    path: "/accounting/currencies",
    subject: SubjectKey.CURRENCY,
  },
  // Sales routes
  {
    path: "/sales/customers",
    subject: SubjectKey.CUSTOMER,
  },
  {
    path: "/sales/reparations",
    subject: SubjectKey.REPARATIONS,
  },
  {
    path: "/sales/invoices",
    subject: SubjectKey.INVOICE,
  },
  // Purchases routes
  {
    path: "/purchases/vendors",
    subject: SubjectKey.VENDOR,
  },
  {
    path: "/purchases/purchase-orders",
    subject: SubjectKey.PURCHASE_ORDER,
  },
  // Inventory routes
  {
    path: "/inventory/stock",
    subject: SubjectKey.INVENTORY_STOCK,
  },
  {
    path: "/inventory/transactions",
    subject: SubjectKey.INVENTORY_TRANSACTION,
  },
  {
    path: "/inventory/products",
    subject: SubjectKey.PRODUCTS,
  },
  {
    path: "/inventory/product-category",
    subject: SubjectKey.PRODUCT_CATEGORY,
  },
  {
    path: "/inventory/brands",
    subject: SubjectKey.BRAND,
  },
  {
    path: "/inventory/services",
    subject: SubjectKey.SERVICE,
  },
  {
    path: "/inventory/service-category",
    subject: SubjectKey.SERVICE_CATEGORY,
  },
  {
    path: "/inventory/location",
    subject: SubjectKey.LOCATION,
  },
  {
    path: "/inventory/barcode",
    subject: SubjectKey.LOCATION, // Note: This seems to use LOCATION subject in the original code
  },
  // Other routes
  {
    path: "/users",
    subject: SubjectKey.USER,
  },
  {
    path: "/reports",
    subject: SubjectKey.REPORTS,
  },
  {
    path: "/settings",
    subject: SubjectKey.SETTINGS,
  },
];

/**
 * Finds the first route that the user has read permission for
 * Returns the dashboard path if user has dashboard permission, otherwise the first accessible route
 */
export const getFirstAccessibleRoute = (): string => {
  // First check if user has dashboard permission
  if (ability.can(PermissionType.READ, SubjectKey.DASHBOARD)) {
    return "/";
  }

  // Find the first route the user can access
  for (const route of menuRoutes) {
    if (ability.can(PermissionType.READ, route.subject)) {
      return route.path;
    }
  }

  // Fallback: if no specific permissions, still allow access to dashboard
  // This handles edge cases where user might have some permissions but none matching our routes
  console.warn("No accessible routes found for user, defaulting to dashboard");
  return "/";
};

/**
 * Checks if the user has permission to access a specific route
 */
export const canAccessRoute = (path: string): boolean => {
  const route = menuRoutes.find(r => r.path === path);
  if (!route) {
    // If route is not in our permission-controlled routes, allow access
    return true;
  }
  return ability.can(PermissionType.READ, route.subject);
};
